import { useState } from "react";
import { Plus, Pencil, Trash2, Inbox } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api, errMsg, useApi } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Field, Empty, Table, Th, Td } from "@/components/kit";
import { PhotoPicker } from "@/components/PhotoPicker";

export function FormFields({ fields, form, set }) {
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      {fields.map((f) => {
        const v = form[f.key] ?? "";
        const tid = `field-${f.key}`;
        let input;
        if (f.type === "select") {
          input = (
            <select data-testid={tid} className="ka-input" value={v} onChange={(e) => set(f.key, e.target.value)}>
              {f.options.map((o) => <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</option>)}
            </select>
          );
        } else if (f.type === "switch") {
          input = (
            <button type="button" data-testid={tid} onClick={() => set(f.key, !v)}
              className={`ka-press rounded-lg border px-3 py-2.5 text-sm font-semibold ${v ? "border-emerald-300 bg-emerald-50 text-emerald-800" : "border-slate-300 bg-white text-slate-600"}`}>
              {v ? f.on || "Yes" : f.off || "No"}
            </button>
          );
        } else if (f.type === "textarea") {
          input = <textarea data-testid={tid} rows={3} className="ka-input" value={v} onChange={(e) => set(f.key, e.target.value)} />;
        } else if (f.type === "photos") {
          input = <PhotoPicker testid={tid} value={v || []} onChange={(ids) => set(f.key, ids)} />;
        } else {
          input = (
            <input data-testid={tid} className="ka-input" type={f.type || "text"} step="any" value={v} placeholder={f.placeholder}
              onChange={(e) => set(f.key, f.type === "number" ? (e.target.value === "" ? null : Number(e.target.value)) : e.target.value)} />
          );
        }
        return <Field key={f.key} label={f.label + (f.required ? " *" : "")} hint={f.hint} className={f.full || f.type === "textarea" || f.type === "photos" ? "sm:col-span-2" : ""}>{input}</Field>;
      })}
    </div>
  );
}

export default function CrudPage({ eyebrow, title, subtitle, resource, columns, fields, defaults, venueScoped, perm = "manage", rowActions, onSaved }) {
  const { can, venueId } = useAuth();
  const { data, reload } = useApi(venueScoped && !venueId ? null : `/${resource}`, venueScoped ? { venue_id: venueId } : undefined);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const editable = can(perm);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const body = { ...form };
    delete body.id;
    if (venueScoped) body.venue_id = venueId;
    try {
      if (form.id) await api.put(`/${resource}/${form.id}`, body);
      else await api.post(`/${resource}`, body);
      toast.success("Saved");
      setForm(null);
      reload();
      onSaved && onSaved();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm("Delete this record? This is logged in the audit trail.")) return;
    try {
      await api.delete(`/${resource}/${row.id}`);
      toast.success("Deleted");
      reload();
      onSaved && onSaved();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const pick = (row) => Object.fromEntries([["id", row.id], ...fields.map((f) => [f.key, row[f.key] ?? defaults[f.key] ?? ""])]);

  return (
    <div>
      <PageHeader eyebrow={eyebrow} title={title} subtitle={subtitle}
        actions={editable && <Button data-testid={`add-${resource}-button`} onClick={() => setForm({ ...defaults })} className="rounded-full bg-emerald-600 hover:bg-emerald-700"><Plus className="mr-1 h-4 w-4" />Add</Button>} />
      {data && data.length === 0 && <Empty testid={`${resource}-empty`} icon={Inbox} title="Nothing here yet" text={editable ? "Add your first record to get started." : "No records yet."} />}
      {data && data.length > 0 && (
        <Table testid={`${resource}-table`}>
          <thead><tr>{columns.map((c) => <Th key={c.key}>{c.label}</Th>)}<Th className="text-right">Actions</Th></tr></thead>
          <tbody>
            {data.map((row) => (
              <tr key={row.id} data-testid={`${resource}-row-${row.id}`} className="hover:bg-slate-50/60">
                {columns.map((c) => <Td key={c.key}>{c.render ? c.render(row) : row[c.key] || "—"}</Td>)}
                <Td className="whitespace-nowrap text-right">
                  {rowActions && rowActions(row, reload)}
                  {editable && <button data-testid={`edit-${resource}-${row.id}`} onClick={() => setForm(pick(row))} className="ka-press rounded-md p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-900"><Pencil className="h-4 w-4" /></button>}
                  {editable && <button data-testid={`delete-${resource}-${row.id}`} onClick={() => remove(row)} className="ka-press rounded-md p-2 text-slate-500 hover:bg-rose-50 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>}
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader><DialogTitle className="font-display text-2xl">{form?.id ? "Edit" : "Add"} {title.toLowerCase()}</DialogTitle></DialogHeader>
          {form && (
            <form onSubmit={save} className="space-y-6">
              <FormFields fields={fields} form={form} set={set} />
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setForm(null)}>Cancel</Button>
                <Button data-testid={`save-${resource}-button`} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">{saving ? "Saving…" : "Save"}</Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
